import React from 'react';

import * as sequtil from 'molbio/SeqUtil';
import {
  RelevantItemsGraphQL,
  RelevantItemsGraphQL_RelevantItemsNode_filesMentioning as FilesMentioningItem,
  RelevantItemsGraphQL_RelevantItemsNode_sequencesUsingAsPrimer_SequenceUsingAsPrimerNode as SequenceUsingAsPrimer,
  RelevantItemsGraphQL_RelevantItemsNode_tagged as TaggedItem,
  RelevantItemsGraphQL_RelevantItemsNode_workflows_WorkflowRelevantItemNode as RelevantWorkflowGQL,
} from 'relevant-items/__generated/RelevantItemsGraphQL';
import getRelevantWorkflowInfo from 'relevant-items/getRelevantWorkflowInfo';
import {
  RelevantItemsFilteredTableState,
  RelevantItemsFilterOption,
} from 'relevant-items/PresentationalRelevantItemsSection';
import {
  buildBenchChipRow,
  getAnyDescriptionMentionRow,
  getBiologicalEntityDescriptionMentionRow,
  getEntryMentionRow,
  getFieldReferenceTaggedRows,
  getSupportedRelevantItems,
  getTaskGroupsRows,
  getWorksheetStepMentionRow,
  isWorksheetStep,
} from 'relevant-items/shared/RelevantItemsRowBuilderUtils';
import {plainReferenceText, RelevantRowData} from 'relevant-items/shared/RelevantItemsTableUtils';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import APIUtil from 'util/APIUtil';
import * as WorkflowActions from 'workflows/WorkflowActions';

type BulkAssemblyItem = RelevantItemsGraphQL['bulkAssembliesUsingAsFragment'][number];
type SourceRunItem = RelevantItemsGraphQL['sourceRuns'][number];

function getEntityLabel(areConvertedRelevantItems: boolean): string {
  return areConvertedRelevantItems ? 'Converted item' : 'Entity';
}

function filterItemsByFilterOption<T extends {id: string; __typename?: string; parentId?: string}>(
  items: Array<T>,
  itemsToShow: RelevantItemsFilterOption
): Array<T> {
  switch (itemsToShow) {
    case RelevantItemsFilterOption.ALL:
      return items;
    case RelevantItemsFilterOption.BULK_ASSEMBLIES:
      return items.filter((item) => item.id && APIUtil.isBulkAssembly(item.id));
    case RelevantItemsFilterOption.ENTRIES:
      return items.filter((item) => item.id && APIUtil.isEntry(item.id));
    case RelevantItemsFilterOption.ENTITY_LINKS:
      return items.filter((item) => item.id && APIUtil.isBiologicalEntity(item.id));
    case RelevantItemsFilterOption.WORKSHEET_STEPS:
      return items.filter((item) => isWorksheetStep(item));
    case RelevantItemsFilterOption.LEGACY_WORKFLOWS:
    case RelevantItemsFilterOption.SEQUENCE_PRIMERS:
    case RelevantItemsFilterOption.PROCESSES:
    case RelevantItemsFilterOption.RUNS:
      return [];
    default:
      throw new Error('Invalid relevant items filter option');
  }
}

function getTaggedRowsForFilter(
  supportedTagged: Array<TaggedItem>,
  itemsToShow: RelevantItemsFilterOption,
  entityLabel: string
): Array<RelevantRowData> {
  return getFieldReferenceTaggedRows(filterItemsByFilterOption(supportedTagged, itemsToShow), entityLabel);
}

function getSequencesUsingAsPrimerRowsForFilter(
  sequencesUsingAsPrimer: Array<SequenceUsingAsPrimer>,
  itemsToShow: RelevantItemsFilterOption,
  entityLabel: string
): Array<RelevantRowData> {
  if (
    itemsToShow !== RelevantItemsFilterOption.ALL &&
    itemsToShow !== RelevantItemsFilterOption.SEQUENCE_PRIMERS
  ) {
    return [];
  }

  return sequencesUsingAsPrimer.map((item): RelevantRowData => {
    const {sequence} = item;
    return buildBenchChipRow(
      sequence,
      `${entityLabel} bound as primer at position: ${
        item.bindStrand === 1 ? `+` : `-`
      }/${sequtil.modelToDisplay(item.bindPosition, sequence.startIndex)}`
    );
  });
}

function getBulkAssemblyRowsForFilter(
  bulkAssembliesUsingAsFragment: Array<BulkAssemblyItem>,
  bulkAssembliesCreatingAsConstruct: Array<BulkAssemblyItem>,
  bulkAssembliesCreatingAsAmplifiedFragment: Array<BulkAssemblyItem>,
  itemsToShow: RelevantItemsFilterOption,
  entityLabel: string
): Array<RelevantRowData> {
  return [
    ...filterItemsByFilterOption(bulkAssembliesUsingAsFragment, itemsToShow).map((item) =>
      buildBenchChipRow(item, `${entityLabel} used as fragment in assembly`)
    ),
    ...filterItemsByFilterOption(bulkAssembliesCreatingAsConstruct, itemsToShow).map((item) =>
      buildBenchChipRow(item, `${entityLabel} created by assembly`)
    ),
    ...filterItemsByFilterOption(bulkAssembliesCreatingAsAmplifiedFragment, itemsToShow).map((item) =>
      buildBenchChipRow(item, `${entityLabel} created by assembly`)
    ),
  ];
}

function getFilesMentioningRowForItem(
  item: FilesMentioningItem,
  itemsToShow: RelevantItemsFilterOption,
  entityLabel: string
): RelevantRowData | null {
  switch (itemsToShow) {
    case RelevantItemsFilterOption.ALL:
      return (
        getWorksheetStepMentionRow(item, entityLabel) ??
        getEntryMentionRow(item, entityLabel) ??
        getAnyDescriptionMentionRow(item, entityLabel)
      );
    case RelevantItemsFilterOption.WORKSHEET_STEPS:
      return getWorksheetStepMentionRow(item, entityLabel);
    case RelevantItemsFilterOption.ENTRIES:
      return getEntryMentionRow(item, entityLabel);
    case RelevantItemsFilterOption.ENTITY_LINKS:
      return getBiologicalEntityDescriptionMentionRow(item, entityLabel);
    case RelevantItemsFilterOption.BULK_ASSEMBLIES:
    case RelevantItemsFilterOption.SEQUENCE_PRIMERS:
    case RelevantItemsFilterOption.LEGACY_WORKFLOWS:
    case RelevantItemsFilterOption.PROCESSES:
    case RelevantItemsFilterOption.RUNS:
      return null;
    default:
      throw new Error('Invalid relevant items filter option');
  }
}

function getFilesMentioningRowsForFilter(
  supportedFilesMentioning: Array<FilesMentioningItem>,
  itemsToShow: RelevantItemsFilterOption,
  entityLabel: string
): Array<RelevantRowData> {
  return supportedFilesMentioning.flatMap((item) => {
    const row = getFilesMentioningRowForItem(item, itemsToShow, entityLabel);
    return row != null ? [row] : [];
  });
}

function getWorkflowRowsForFilter(
  workflows: Array<RelevantWorkflowGQL>,
  itemsToShow: RelevantItemsFilterOption,
  areConvertedRelevantItems: boolean
): Array<RelevantRowData> {
  if (
    itemsToShow !== RelevantItemsFilterOption.ALL &&
    itemsToShow !== RelevantItemsFilterOption.LEGACY_WORKFLOWS
  ) {
    return [];
  }

  return workflows.map((item): RelevantRowData => {
    const {description, modifiedAt, name, projectName} = getRelevantWorkflowInfo(
      item,
      areConvertedRelevantItems
    );

    return {
      id: item.workflow.id,
      name,
      referencedInText: description,
      modifiedAt,
      location: projectName,
      chip: (
        <Chip
          size={ChipSize.SMALL}
          text={name}
          icon="workflow"
          className="chip-container"
          onClick={() => {
            WorkflowActions.routeToWorkflow(item.workflow.id);
          }}
        />
      ),
    };
  });
}

function getTaskGroupsRowsForFilter(
  processOutputs: RelevantItemsGraphQL['processOutputs'],
  processTasks: RelevantItemsGraphQL['processTasks'],
  itemsToShow: RelevantItemsFilterOption,
  entityLabel: string
): Array<RelevantRowData> {
  if (itemsToShow !== RelevantItemsFilterOption.ALL && itemsToShow !== RelevantItemsFilterOption.PROCESSES) {
    return [];
  }

  return getTaskGroupsRows(processOutputs, processTasks, entityLabel);
}

function getSourceRunsRowsForFilter(
  sourceRuns: Array<SourceRunItem>,
  itemsToShow: RelevantItemsFilterOption
): Array<RelevantRowData> {
  if (itemsToShow !== RelevantItemsFilterOption.ALL && itemsToShow !== RelevantItemsFilterOption.RUNS) {
    return [];
  }

  return sourceRuns.map(
    (item): RelevantRowData => ({
      id: item.apiId,
      name: item.name,
      referencedInText: plainReferenceText('Analysis created from run'),
      modifiedAt: item.modifiedAt,
      location: item.schema?.name ?? null,
      chip: <BenchChip id={item.apiId} size={ChipSize.SMALL} />,
    })
  );
}

function getBaseEntityRelevantItemRowData(
  itemsToShow: RelevantItemsFilterOption,
  areConvertedRelevantItems: boolean,
  relevantItemsGraphQL: RelevantItemsGraphQL | null
): Array<RelevantRowData> {
  if (relevantItemsGraphQL == null) {
    return [];
  }

  const entityLabel = getEntityLabel(areConvertedRelevantItems);
  const {
    bulkAssembliesCreatingAsAmplifiedFragment,
    bulkAssembliesCreatingAsConstruct,
    bulkAssembliesUsingAsFragment,
    filesMentioning,
    processOutputs,
    processTasks,
    sequencesUsingAsPrimer,
    sourceRuns,
    tagged,
    workflows,
  } = relevantItemsGraphQL;

  const {supportedTagged, supportedFilesMentioning} = getSupportedRelevantItems(tagged, filesMentioning);

  return [
    ...getTaggedRowsForFilter(supportedTagged, itemsToShow, entityLabel),
    ...getSequencesUsingAsPrimerRowsForFilter(sequencesUsingAsPrimer, itemsToShow, entityLabel),
    ...getBulkAssemblyRowsForFilter(
      bulkAssembliesUsingAsFragment,
      bulkAssembliesCreatingAsConstruct,
      bulkAssembliesCreatingAsAmplifiedFragment,
      itemsToShow,
      entityLabel
    ),
    ...getFilesMentioningRowsForFilter(supportedFilesMentioning, itemsToShow, entityLabel),
    ...getWorkflowRowsForFilter(workflows, itemsToShow, areConvertedRelevantItems),
    ...getTaskGroupsRowsForFilter(processOutputs, processTasks, itemsToShow, entityLabel),
    ...getSourceRunsRowsForFilter(sourceRuns, itemsToShow),
  ];
}

export function getEntityRelevantItemRowData(props: {
  relevantItemsGraphQL: RelevantItemsGraphQL;
  relevantConvertedFromItemsGraphQL: RelevantItemsGraphQL | null;
  filteredTableState: RelevantItemsFilteredTableState;
}): Array<RelevantRowData> {
  const itemsToShow = props.filteredTableState.filterOption;

  return [
    ...getBaseEntityRelevantItemRowData(itemsToShow, false, props.relevantItemsGraphQL),
    ...getBaseEntityRelevantItemRowData(itemsToShow, true, props.relevantConvertedFromItemsGraphQL),
  ];
}
